import posthog from "posthog-js";
import type { UserResponse } from "@/app/api/types/user";
import type { CompanyResponse } from "@/app/api/types/company";
import type { GroupResponse } from "@/app/api/types/group";

// Central PostHog wrapper. All `posthog.*` calls outside `posthog-provider.tsx`
// MUST go through this module so:
//   - person + group identity stay consistent (every event carries tenant context)
//   - we can no-op cleanly when PostHog isn't initialized (dev, missing env)
//   - we have a single place to evolve the tracking plan

// PostHog group type used for tenant-level filtering. PostHog Group Analytics
// scales to thousands of group keys per type, which is the right shape for our
// multi-tenant data (a single PostHog project, filterable by company).
const TENANT_GROUP_TYPE = "company";

// Second group type: the seller group (team) the user belongs to. Lets
// dashboards roll metrics up company → seller group → seller without
// per-event instrumentation.
const SELLER_GROUP_TYPE = "group";

// Readiness is tracked with a module-level flag flipped from `posthog-provider`
// inside the `loaded` callback — that callback is the documented stable hook
// for "init() has finished". The previous version guarded on `posthog.__loaded`,
// which works today but is an internal SDK field with no stability guarantee
// across major versions.
let _ready = false;

export function markAnalyticsReady(): void {
  _ready = true;
}

function isReady(): boolean {
  return _ready;
}

// Deployment-stable super properties (environment, app version). They ride on
// every event and must be re-registered after `posthog.reset()` since reset
// wipes the persistence store — otherwise the first logout permanently
// strips these dimensions for the rest of the browser session on shared
// devices (warehouse terminals, kitchen tablets).
export function registerDeploymentProperties(): void {
  if (!isReady()) return;
  const environment = import.meta.env.VITE_POSTHOG_ENVIRONMENT;
  const appVersion = import.meta.env.VITE_SENTRY_RELEASE;
  posthog.register({
    ...(environment ? { environment } : {}),
    ...(appVersion ? { app_version: appVersion } : {}),
  });
}

// Fingerprint of the last (person + traits) sent to PostHog via identify().
// `setPostHogUserContext` re-runs on every settle of the company/groups/employee
// queries, so in the offline-first app identifyUser is called on every
// `sync.resync` (hundreds per session) with an unchanged user. Re-sending an
// identical identify each time is pure churn, and re-issuing identity
// transactions mid-session can disturb the active session recording (a repeated
// identify was one cause of replays landing without their initial full snapshot,
// "the snapshot never reached PostHog"). Cleared by `resetAnalytics()`.
let _lastIdentity: string | null = null;

export function identifyUser(user: UserResponse): void {
  if (!isReady()) return;
  const traits = {
    email: user.email,
    name: user.full_name || user.email,
    language: user.language,
    company_id: user.company_id,
    feature_permissions: user.feature_permissions,
    company_permissions: user.company_permissions,
    default_location_id: user.default_location_id,
    employee_id: user.employee_id,
  };
  // Send identify only when the person OR any trait actually changes: the first
  // identify, a genuine profile update (name/permission/language/…), or a user
  // switch. identify() is the ONLY call that writes these to the person profile
  // (register/group only refresh super properties used for event filtering), so
  // gating on a byte-for-byte fingerprint keeps the profile fresh while dropping
  // the per-resync churn. `resetAnalytics()` clears the fingerprint so the next
  // user — or the same user after a logout that reset the distinct_id — is
  // re-identified exactly once.
  const fingerprint = `${user.id}:${JSON.stringify(traits)}`;
  if (fingerprint === _lastIdentity) return;
  _lastIdentity = fingerprint;
  posthog.identify(user.id, traits);
}

// A user can belong to several seller groups, but PostHog supports a single
// active group per type — so events are attributed to one deterministic
// "primary" group: the oldest by creation date (ties broken by id). A
// multi-group user's events land on their longest-standing team instead of
// flapping with API response ordering. Groups that don't contain the user
// (as owner or member) are ignored: the my-groups query cache is not
// user-scoped, so right after an in-app user switch it can briefly serve
// the previous user's groups — filtering by membership keeps those from
// being attributed to the new user.
export function pickPrimaryGroup(
  groups: GroupResponse[] | undefined,
  userId: string,
): GroupResponse | null {
  const mine = groups?.filter(
    (g) => g.owner_id === userId || g.members.some((m) => m.user_id === userId),
  );
  if (!mine?.length) return null;
  return mine.sort(
    (a, b) =>
      a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id),
  )[0];
}

export function setSellerGroup(group: GroupResponse): void {
  if (!isReady()) return;
  posthog.group(SELLER_GROUP_TYPE, group.id, {
    name: group.name,
    owner_id: group.owner_id,
    member_count: group.members.length,
    created_at: group.created_at,
  });
}

export function setTenantGroup(company: CompanyResponse): void {
  if (!isReady()) return;
  posthog.group(TENANT_GROUP_TYPE, company.id, {
    name: company.name,
    country: company.country,
    city: company.city,
    timezone: company.timezone,
    created_at: company.created_at,
  });
}

// Group membership is persisted by the PostHog SDK and `posthog.group()` can
// only set, never unset, a single type. Without an explicit clear, a user
// with no seller group keeps whatever `group` association the store last
// held (previous user on a shared device, or their own membership after
// being removed from the team). The only removal primitive the SDK offers
// is resetGroups(), which wipes ALL types — so the tenant group has to be
// re-attached afterwards.
//
// Re-attach ONLY a caller-supplied, user-verified `tenant` (with its full
// props), never the raw key sitting in the PostHog store: that key can
// belong to the previous user after an in-app switch (the store survives a
// switch since we don't reset() then) or after a crash + relogin that
// hydrated a stale `["company","me"]` cache. Restoring it would tie the new
// user's events to the wrong company. When no trusted tenant is available we
// leave the tenant group detached; the observability effect re-establishes
// it on its next fire once the company query settles for this user.
export function clearSellerGroup(tenant?: CompanyResponse | null): void {
  if (!isReady()) return;
  const groups = posthog.getGroups();
  if (!(SELLER_GROUP_TYPE in groups)) return;
  posthog.resetGroups();
  if (tenant) {
    setTenantGroup(tenant);
  }
}

// Drop the tenant (company) group when we can't trust the current identity's
// company. Same mechanics + reasoning as clearSellerGroup: posthog.group() can
// only set, never unset, and the SDK's only removal primitive is resetGroups(),
// which wipes ALL types. So this is a no-op unless a company group is actually
// present, and callers MUST run it BEFORE (re-)setting the seller group, since
// resetGroups() also clears the seller association — the seller handling that
// follows re-attaches it from a trusted source.
//
// Never re-attach the company key sitting in the store: after a stale
// ["company","me"] hydrate (crash + relogin) that key can be the PREVIOUS
// user's company, which would tie the new user's events to the wrong tenant.
// Leaving it detached is safe — the observability effect re-establishes the
// right tenant group on its next fire once the company query settles for this
// user.
export function clearTenantGroup(): void {
  if (!isReady()) return;
  const groups = posthog.getGroups();
  if (!(TENANT_GROUP_TYPE in groups)) return;
  posthog.resetGroups();
}

// Super properties ride on every event automatically. Use them for the
// always-relevant dimensions ("who/where", not "what") so dashboards can
// filter without needing per-event instrumentation.
export function registerSuperProperties(props: {
  company_id?: string | null;
  user_id?: string;
  feature_permissions?: number;
  company_permissions?: number | null;
  employee_id?: string | null;
  employee_code?: string | null;
  group_id?: string | null;
}): void {
  if (!isReady()) return;
  posthog.register(props);
}

// Suppress ALL capture — events, autocapture, and session replay — and persist
// the choice in PostHog's `__opt_in_out` store so it survives a full page
// reload. Used for super-admin impersonation: the app authenticates AS the
// customer, so PostHog would otherwise identify as them (their distinct_id,
// person, `company` group) with no marker it's an admin, polluting the real
// user's events + replays. `reset()` clears any prior opt-out state, so this
// MUST be called AFTER any reset in the same flow, not before.
export function optOutAnalytics(): void {
  if (!isReady()) return;
  posthog.opt_out_capturing();
}

// Re-enable capture and clear the persisted opt-out. Since opt-out is sticky in
// storage, a non-impersonated session MUST call this to guarantee capturing —
// otherwise a browser that previously impersonated stays silently opted out.
// Guard on has_opted_out_capturing() so this is a no-op for the common path
// (user was never opted out), avoiding a spurious $opt_in event on every boot.
export function optInAnalytics(): void {
  if (!isReady()) return;
  if (!posthog.has_opted_out_capturing()) return;
  posthog.opt_in_capturing({ captureEventName: false });
}

export function resetAnalytics(): void {
  if (!isReady()) return;
  // `reset()` clears the distinct_id, group memberships, and super properties
  // — call on logout / auth error so the next user starts clean.
  posthog.reset();
  // reset() clears the distinct_id, so the identify fingerprint must be cleared
  // too — otherwise the next identifyUser (even for the same user logging back
  // in) would match the stale fingerprint and skip re-identifying on a store
  // that no longer knows who they are.
  _lastIdentity = null;
  // Re-register deployment-stable props that reset() just wiped. Without this,
  // every event after the first logout in a browser session loses
  // `environment` and `app_version` until a full page reload.
  registerDeploymentProperties();
}

// Where a pre-order line/header edit happened. `recap_modify` = the recap
// modify-buffer batch save (PENDING/AI_PROPOSAL, focus view + proposal cards;
// includes new orders created via "New Order", which open PENDING in the focus
// view); `po_detail` = the order-detail focus-view header form; `recap_list` =
// the per-order header strip (`PreOrderHeader`) shown inside the recap list /
// partner cards, whose comment popover stays editable for PENDING / proposal
// orders (the focus page swaps it for `po_detail`, so the two never overlap).
export type PreOrderEditSurface = "recap_modify" | "po_detail" | "recap_list";

// Which recap workspace an interaction fired from. Sales (`/recap`), purchases
// (`/purchases`) and transport (`/recap_transport`) all mount the SAME recap
// shell, so without this dimension their events are indistinguishable in
// PostHog. Optional on every event: callsites outside a recap workspace (the
// query/mutation layer, the standalone AI queue) simply omit it.
//
// NOTE: this union mirrors `RecapModeConfig` (app/recap/config/types.ts). They
// are kept as separate definitions to avoid a lib→feature import, but parity is
// enforced at compile time: every recap callsite assigns a `RecapModeConfig`
// value into one of these `mode` fields, so adding a recap mode without adding
// it here fails typecheck rather than silently dropping the value.
export type RecapWorkspaceMode = "sales" | "purchases" | "transport";

// Typed event catalog. Adding an entry here is the only way to call `track()` —
// keeps event naming consistent ("domain_action") and lets us evolve property
// shapes without grep-then-pray. Lowercase snake_case is the PostHog convention.
type EventMap = {
  // Purchase creation/confirmation have NO dedicated events on purpose: the
  // server-side `order_confirmed` / `order_exported` (V2 analytics-subscriber)
  // carry `category`, which already splits sales/purchases/transport.
  // Manual order creation (recap "New order" + the transport new-order button).
  // Fires on the create mutation's success — on the offline-first path that's
  // the optimistic local apply. `mode` distinguishes sales/purchases/transport.
  order_created: {
    order_id: string;
    partner_id: string | null;
    order_type: number;
    mode?: RecapWorkspaceMode;
  };
  // Per-flow confirmation. Emitted by the modify-buffer save (StatusCell
  // click-cycle → buffered status=1 → flushSave onSuccess) and the legacy
  // `useConfirmFlow` mutation.
  pre_order_flow_confirmed: {
    flow_id: string;
    pre_order_id: string;
  };
  // AI adoption funnel. `scope` distinguishes the per-PO resolve-all
  // gesture ("all": recap / queue Accept-Decline on one pre-order) from
  // the cross-PO bulk queue resolve ("bulk"). `flow_count` is the number
  // of AI-touched flows resolved, when the callsite has it cheap.
  // `order_kind` splits a whole AI-created order (status AI_PROPOSAL) from
  // AI modifications staged on an existing non-AI order — the two distinct
  // "Reject AI proposal" vs "Reject AI modifications" affordances.
  ai_proposal_accepted: {
    pre_order_id: string;
    scope: "all" | "bulk";
    flow_count?: number;
    mode?: RecapWorkspaceMode;
    order_kind?: "ai_created" | "ai_modified";
  };
  ai_proposal_declined: {
    pre_order_id: string;
    scope: "all" | "bulk";
    flow_count?: number;
    mode?: RecapWorkspaceMode;
    order_kind?: "ai_created" | "ai_modified";
  };
  // AI chat usage — fires once per sent message: the composer (Send button and
  // Enter-to-send, which share one handler) plus imperative handoff sends
  // (home-page prompts, etc.). Fired only after a successful session bootstrap,
  // so failed sends never count. NO message text. `has_media` = the message
  // carried file attachments.
  ai_chat_message_sent: {
    has_media: boolean;
  };
  // ERP export RETRY — user explicitly re-fired a failed export. The confirm
  // and the export success/failure themselves are tracked authoritatively
  // server-side (`order_confirmed` on confirm, `order_exported` on the ERP
  // readback); a client "export requested" event would just duplicate
  // `order_confirmed` for the same gesture, so it's intentionally not emitted.
  export_retried: {
    pre_order_id: string;
  };
  // Search / filter friction signals. NO query text — PII + unbounded
  // cardinality. `filter` is a low-cardinality enum-ish name (e.g.
  // "status_confirmed", "view_exported"), never a user-typed value.
  search_performed: {
    surface: string;
    mode?: RecapWorkspaceMode;
  };
  filter_applied: {
    surface: string;
    filter: `status_${string}` | `view_${string}`;
    mode?: RecapWorkspaceMode;
  };
  // Pre-order editing engagement. `field` is the column / header name only
  // (low cardinality, e.g. "price_per_weight", "delivery_date") — never the
  // quantity, price, or any user-typed value. One event per committed action.
  //
  // TIMING — two intentionally different success points, matching how each
  // write reaches the server:
  //  - LINE events ride the modify-buffer `pushBatch`, whose `onSuccess` fires
  //    after the POST + reconcile, so they are SERVER-CONFIRMED.
  //  - `pre_order_header_changed` rides the single-field `useUpdatePreOrder`
  //    mutation, whose `onSuccess` fires on the OPTIMISTIC Dexie apply (the
  //    server POST is queued + flushed fire-and-forget by the SyncEngine).
  //    This matches the app's offline-first success model (optimistic apply is
  //    what the user sees as "done"; reverts are rare and roll back the row),
  //    but it means a later server revert is NOT subtracted from header counts.
  //    Treat header-edit counts as "edits accepted locally", not "committed".
  pre_order_line_added: {
    pre_order_id: string;
    surface: PreOrderEditSurface;
    mode?: RecapWorkspaceMode;
  };
  pre_order_line_removed: {
    pre_order_id: string;
    surface: PreOrderEditSurface;
    mode?: RecapWorkspaceMode;
  };
  pre_order_line_updated: {
    pre_order_id: string;
    field: string;
    surface: PreOrderEditSurface;
    mode?: RecapWorkspaceMode;
  };
  pre_order_header_changed: {
    pre_order_id: string;
    field: string;
    surface: PreOrderEditSurface;
    mode?: RecapWorkspaceMode;
  };
  // AI guidance overlay funnel (lib/guide). `guide_id` is the per-delivery
  // uuid minted by the guide_user tool; `walkthrough_id` is set for registry walkthroughs.
  // `guide_target_missing` + `guide_payload_rejected` are the fail-fast drift
  // signals — a spike means a data-testid was renamed or the AI authored a
  // bad payload; both surface visibly to the user too (never silent).
  guide_started: {
    guide_id: string;
    walkthrough_id: string | null;
    source: "ai" | "registry" | "replay";
    step_count: number;
  };
  guide_step_viewed: {
    guide_id: string;
    step_index: number;
    target: string | null;
  };
  guide_completed: {
    guide_id: string;
    walkthrough_id: string | null;
    source: "ai" | "registry" | "replay";
    step_index: number;
    step_count: number;
  };
  guide_dismissed: {
    guide_id: string;
    walkthrough_id: string | null;
    source: "ai" | "registry" | "replay";
    step_index: number;
    step_count: number;
  };
  guide_target_missing: {
    guide_id: string;
    step_index: number;
    target: string;
  };
  guide_payload_rejected: {
    guide_id: string | null;
    walkthrough_id: string | null;
    reason: string;
  };
  // A walkthrough was launched outside its required context (its `requires` marker was
  // absent) — e.g. batch-edit started outside an editable order.
  guide_precondition_unmet: {
    guide_id: string;
    walkthrough_id: string;
    requires: string;
  };
};

export function track<K extends keyof EventMap>(
  name: K,
  props: EventMap[K],
): void {
  if (!isReady()) return;
  posthog.capture(name, props);
}

// Operational / diagnostic events from the offline SyncEngine (db-closed,
// resync lifecycle, transport reconnects) and its consumers. Kept OUTSIDE the
// typed product catalog above on purpose: these are open-ended infra signals
// emitted from `@stockline/web` (which can't import this module), funnelled here
// so they still ride the per-tenant super-properties + `company` group that
// every `posthog.capture` carries. Names are namespaced ("offline.*", "sync.*",
// "prices.*"); keep them low-cardinality and low-volume.
export function trackOperational(
  name: string,
  props?: Record<string, unknown>,
): void {
  if (!isReady()) return;
  posthog.capture(name, props);
}
